import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router'
import { courses, type Course } from '@/lib/courses'
import { CourseCard } from '@/components/CourseCard'
import { BrandMark, ToolTag } from '@/components/ToolLogo'
import { brandTools, SOCIAL_ICONS, type BrandTool } from '@/lib/toolIcons'
import { buildCampaignWhatsAppUrl, buildWhatsAppUrl, siteConfig } from '@/lib/siteConfig'
import { getEnrollment } from '@/lib/intake'
import { useCurrentTime } from '@/lib/hydration'
import { cn } from '@/lib/utils'
import { SiteFooter } from '@/components/layout/SiteFooter'
import { SiteHeader } from '@/components/layout/SiteHeader'
import { Aurora } from '@/components/design/Aurora'
import { CountUp } from '@/components/design/CountUp'
import { FaqList } from '@/components/design/FaqList'
import { HandArrow, HandCircle, HandNote, HandUnderline } from '@/components/design/HandDrawn'
import { Marquee } from '@/components/design/Marquee'
import {
  AgentChat,
  ClassCallMock,
  InstallmentBar,
  LiveDot,
  OnlineDot,
  PortalMock,
  ProjectStack,
  SupportChat,
} from '@/components/design/Mockups'
import { PathFinder } from '@/components/design/PathFinder'
import { Reveal } from '@/components/design/Reveal'
import { RoadmapPath } from '@/components/design/RoadmapPath'
import { SeatMeter } from '@/components/design/SeatMeter'
import { Accent, Gold, Muted, SectionLabel } from '@/components/design/SectionLabel'
import { StoryTimeline } from '@/components/design/StoryTimeline'
import { TrustNotes } from '@/components/design/TrustNotes'
import { useSpotlight, useTilt } from '@/components/design/useReveal'
import {
  IconArrowRight,
  IconArrowUpRight,
  IconBriefcase,
  IconCheck,
  IconCode,
  IconGlobe,
  IconGraduationCap,
  IconLayers,
  IconMessageCircle,
  IconPhone,
  IconShieldCheck,
  IconSparkles,
  IconTerminal,
  IconUsers,
  IconX,
} from '@/components/Icons'

function getHomeFaqs(batchMonth: string) {
  return [
    {
      question: 'What is Skillo Academy?',
      answer:
        'Skillo Academy is a live mentor-led tech academy in Sylhet, Bangladesh, teaching AI Agent Building, Full Stack Web Development with AI, practical digital skills, and foundation computer/English skills in Bangla + English. Courses focus on skill-building and portfolio work — not income guarantees.',
    },
    {
      question: 'Where is Skillo Academy located?',
      answer: `Skillo Academy is based in ${siteConfig.contact.location}, Sylhet, Bangladesh.`,
    },
    {
      question: 'Does Skillo Academy teach in Bangla?',
      answer:
        'Yes. Skillo teaches in Bangla + English so students can understand lessons clearly while building technical English confidence.',
    },
    {
      question: 'Which course is best for beginners?',
      answer:
        'Absolute beginners should start with Basic Computer & English. Students with computer confidence can choose Full Stack Web Development with AI for coding or AI Agent Builder for practical automation and AI agent skills.',
    },
    {
      question: 'Does Skillo guarantee freelancing income or jobs?',
      answer: siteConfig.trust.incomeDisclaimer,
    },
    {
      question: 'How can I enroll?',
      answer:
        'Open a course page, fill the enrollment form, or contact Skillo directly on WhatsApp for counselling, batch placement, and payment guidance.',
    },
    {
      question: 'Is there a free orientation class?',
      answer:
        'Yes. Skillo runs a free career orientation path where students can compare AI Agent Builder and Full Stack Web Development before choosing a paid course.',
    },
    {
      question: 'Is installment support available?',
      answer: `Yes. The current ${batchMonth} campaign installment guideline is ${siteConfig.campaign.installment}. Final payment details are confirmed during counselling.`,
    },
    {
      question: 'Can guardians join counselling?',
      answer:
        'Yes. Guardians can join counselling to understand course fit, schedule, payment plan, and realistic outcomes.',
    },
    {
      question: 'What if my computer or English is weak?',
      answer:
        'Skillo may recommend Basic Computer & English first if you need confidence with files, internet, email, typing, Google Workspace, and practical English before advanced tech courses.',
    },
  ]
}

const SKILLS = [
  { title: 'Prompting & AI APIs', body: 'OpenAI, Claude and Gemini inside real tools.' },
  { title: 'Automation with n8n', body: 'Workflows that answer, sort and remind.' },
  { title: 'React & Next.js', body: 'Interfaces people can actually use.' },
  { title: 'APIs & databases', body: 'Node.js, auth, MongoDB and payments.' },
  { title: 'Shipping to the web', body: 'Deploy, debug and keep it running.' },
  { title: 'Portfolio & tech English', body: 'Show your work and explain it well.' },
]

const PERSONAS = [
  {
    id: 'beginner',
    label: 'I am a total beginner',
    icon: <IconGraduationCap size={18} />,
    slug: 'basic-computer-english',
    reason:
      'Start with Basic Computer & English if you need confidence with computers, internet, email, typing, Google Workspace, and practical English before technical training.',
  },
  {
    id: 'web',
    label: 'I want to build websites',
    icon: <IconCode size={18} />,
    slug: 'full-stack-ai-web-development',
    reason:
      'Choose Full Stack Web Development with AI to build websites, apps, SaaS products, APIs, payment flows, and portfolio projects with React and Next.js.',
  },
  {
    id: 'ai',
    label: 'I want practical AI skills',
    icon: <IconSparkles size={18} />,
    slug: 'ai-agent-builder-beginner-to-pro',
    reason:
      'Choose AI Agent Builder for n8n workflows, chatbots, WhatsApp agents, and RAG systems you can use for studies, projects, jobs, startups, or optional freelancing later.',
  },
  {
    id: 'coder',
    label: 'I already write code',
    icon: <IconTerminal size={18} />,
    slug: 'agentic-ai-engineer-python',
    reason:
      'Plan for AI Agent Engineering with Python later if you want LangChain, LangGraph, RAG, FastAPI, Docker, and production-grade AI agent systems.',
  },
]

const PROMISES = [
  'Live classes with a mentor who knows your name',
  'Feedback on the tasks you submit',
  'Projects you can show in your portfolio',
  'Honest course-fit counselling, guardians welcome',
]

const NEVER = [
  'Guaranteed freelancing income',
  'Guaranteed jobs or salaries',
  'Fake reviews or inflated numbers',
]

const PRIMARY_PILL = cn(
  'group relative inline-flex min-h-[52px] items-center justify-center gap-2 overflow-hidden rounded-full bg-[var(--color-brand-accent)] px-7 text-[15px] font-semibold text-[var(--accent-ink)] no-underline',
  'shadow-[0_0_0_1px_rgba(0,229,255,0.5),0_14px_44px_-12px_rgba(0,229,255,0.75)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_0_1px_rgba(0,229,255,0.7),0_20px_56px_-10px_rgba(0,229,255,0.9)]',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-bg)]',
)

const GLASS_PILL =
  'inline-flex min-h-[52px] items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-6 text-[15px] font-semibold text-[var(--text-strong)] no-underline backdrop-blur transition-[border-color,background-color,box-shadow] duration-300 hover:border-white/25 hover:bg-white/[0.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)]'

const WHITE_PILL = cn(
  'group inline-flex min-h-[54px] items-center justify-center gap-2 rounded-full bg-white px-8 text-[15px] font-semibold text-[#07080a] no-underline',
  'shadow-[0_0_0_1px_rgba(255,255,255,0.6),0_18px_60px_-14px_rgba(0,229,255,0.65)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_0_1px_rgba(255,255,255,0.8),0_24px_70px_-12px_rgba(0,229,255,0.85)]',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-bg)]',
)

const CARD =
  'spotlight-card relative overflow-hidden rounded-[24px] border border-[var(--surface-border)] bg-[linear-gradient(180deg,#0f1216,#0b0d10)]'

function delay(ms: number) {
  return { '--rise-delay': `${ms}ms` } as CSSProperties
}

function Chapter({
  index,
  label,
  id,
  title,
  intro,
  className,
  children,
}: {
  index: number
  label: string
  id: string
  title: ReactNode
  intro?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <section aria-labelledby={id} className={cn('relative py-20 sm:py-24', className)}>
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6">
        <Reveal>
          <SectionLabel index={index}>{label}</SectionLabel>
        </Reveal>
        <Reveal delay={80}>
          <h2
            id={id}
            className="mt-8 max-w-4xl text-balance font-display text-[clamp(32px,4.6vw,58px)] font-semibold leading-[1.04] tracking-[-0.04em] text-[var(--text-strong)]"
          >
            {title}
          </h2>
        </Reveal>
        {intro && (
          <Reveal delay={140}>
            <div className="mt-6 max-w-2xl text-base leading-relaxed text-[var(--surface-muted)] sm:text-lg">
              {intro}
            </div>
          </Reveal>
        )}
        <div className="mt-14 sm:mt-16">{children}</div>
      </div>
    </section>
  )
}

const BUILD_CATEGORIES = new Set(['Web Development', 'Mobile'])

function toolRows() {
  const open = courses
    .filter((course) => course.status === 'open')
    .sort((a, b) => a.priority - b.priority)
  const build = brandTools(
    open
      .filter((course) => BUILD_CATEGORIES.has(course.category))
      .flatMap((course) => course.tools),
  )
  const taken = new Set(build.map((tool) => tool.name))
  const automate = brandTools(
    open
      .filter((course) => !BUILD_CATEGORIES.has(course.category))
      .flatMap((course) => course.tools),
  ).filter((tool) => !taken.has(tool.name))
  return [
    { label: 'Web and mobile tools taught in the open courses', tools: build },
    { label: 'AI, automation and everyday tools taught in the open courses', tools: automate },
  ].filter((row) => row.tools.length > 0)
}

function ToolChip({ tool }: { tool: BrandTool }) {
  return (
    <span className="inline-flex items-center gap-2.5 rounded-full border border-white/[0.08] bg-white/[0.03] py-2 pl-3 pr-4 text-[13px] font-medium text-[var(--surface-fg)] transition-colors duration-300 hover:border-white/20 hover:bg-white/[0.06]">
      <BrandMark icon={tool.icon} size={16} />
      {tool.name}
    </span>
  )
}

function HeroPhoto({ enrollmentSeats }: { enrollmentSeats: string }) {
  const tiltRef = useTilt<HTMLDivElement>(7)

  return (
    <div className="relative mx-auto mt-6 w-full max-w-[440px] sm:mt-16 lg:mx-0 lg:mt-4 lg:justify-self-end">
      <div
        aria-hidden="true"
        className="absolute -inset-12 rounded-[3rem] bg-[radial-gradient(circle_at_50%_40%,rgba(0,229,255,0.24),transparent_62%)]"
      />
      <div
        className="animate-blur-rise absolute -top-24 left-0 z-20 hidden w-64 -rotate-3 sm:block lg:-left-16"
        style={delay(700)}
      >
        <HandNote>Hi, I&apos;m Minhaj. I teach live classes in Sylhet.</HandNote>
        <HandArrow
          variant="curve-down-right"
          draw="load"
          delay={1300}
          className="ml-40 h-10 w-14 text-[var(--marker)]"
        />
      </div>
      <div
        ref={tiltRef}
        className="animate-blur-rise relative [perspective:1200px]"
        style={delay(260)}
      >
        <div className="relative transition-transform duration-500 ease-[var(--ease-out-expo)] [transform:rotateX(var(--tilt-x,0deg))_rotateY(var(--tilt-y,0deg))] [transform-style:preserve-3d]">
          <figure className="relative overflow-hidden rounded-[30px] border border-white/10 bg-[var(--surface-card)] shadow-[0_50px_120px_-40px_rgba(0,0,0,0.95)]">
            <span aria-hidden="true" className="border-beam z-10 [--beam-duration:9s]" />
            <img
              src="/images/sylhet.jpg"
              width={1536}
              height={2048}
              alt="Minhaj Sadik, founder and lead instructor of Skillo Academy"
              fetchPriority="high"
              className="aspect-[4/5] w-full origin-[50%_52%] scale-[1.55] object-cover object-[50%_48%] [filter:saturate(0.9)_contrast(1.06)_brightness(0.9)]"
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-[linear-gradient(160deg,rgba(0,229,255,0.14),transparent_42%),radial-gradient(ellipse_at_50%_40%,transparent_45%,rgba(7,8,10,0.6))]"
            />
            <div
              aria-hidden="true"
              className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#07080a] via-[#07080a]/60 to-transparent"
            />
            <figcaption className="glass absolute left-4 top-4 rounded-2xl px-3.5 py-2.5">
              <span className="flex items-center gap-2 font-display text-[15px] font-semibold text-[var(--text-strong)]">
                Minhaj Sadik
                <OnlineDot className="size-2" />
              </span>
              <span className="block text-[12px] text-[var(--surface-muted)]">
                Founder &amp; lead instructor · {siteConfig.contact.location}
              </span>
            </figcaption>
          </figure>
        </div>
      </div>
      <div
        className="glass animate-float absolute -right-4 top-[38%] z-20 hidden rounded-2xl px-4 py-3 shadow-[var(--shadow-lg)] sm:block xl:-right-10"
        style={{ '--float-delay': '400ms' } as CSSProperties}
      >
        <p className="font-code text-[10px] uppercase tracking-[0.18em] text-[var(--surface-muted)]">
          Seat token
        </p>
        <p className="mt-1 font-display text-xl font-semibold text-[var(--text-strong)]">
          {siteConfig.campaign.seatBookingToken}
        </p>
      </div>
      <div
        className="glass animate-float absolute -right-3 bottom-[16%] z-20 hidden items-center gap-2.5 rounded-2xl px-3.5 py-2.5 shadow-[var(--shadow-lg)] sm:flex xl:-right-12"
        style={{ '--float-delay': '1400ms' } as CSSProperties}
      >
        <LiveDot />
        <span className="text-[13px] font-semibold text-[var(--text-strong)]">
          Live classes · max {enrollmentSeats} seats
        </span>
      </div>
      <AgentChat className="animate-float absolute -bottom-20 -left-20 z-30 hidden min-[1400px]:block [--float-delay:900ms]" />
    </div>
  )
}

function Hero() {
  const now = useCurrentTime()
  const enrollment = getEnrollment(now)
  const spotlightRef = useSpotlight<HTMLElement>()
  const rows = toolRows()

  return (
    <section
      ref={spotlightRef}
      className="spotlight-area relative overflow-hidden pb-14 pt-32 sm:pt-36 lg:pb-16"
    >
      <Aurora tone="hero" />
      <div className="relative mx-auto grid max-w-[1240px] items-center gap-12 px-4 sm:px-6 lg:grid-cols-[minmax(0,1.32fr)_minmax(0,0.68fr)] lg:gap-12">
        <div>
          <p
            className="animate-blur-rise inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] py-1.5 pl-2 pr-4 text-[13px] font-medium text-[var(--surface-fg)] backdrop-blur"
            style={delay(0)}
          >
            <OnlineDot />
            {enrollment.batchName} Now Enrolling
            <span aria-hidden="true" className="h-3.5 w-px bg-white/15" />
            <span className="text-[var(--surface-muted)]">Starts {enrollment.nextBatchDate}</span>
          </p>

          <h1 className="mt-8 font-display text-[clamp(34px,4.1vw,56px)] font-semibold leading-[1.04] tracking-[-0.045em]">
            <span className="animate-blur-rise block text-gradient-white" style={delay(80)}>
              AI Agent, Web Development
            </span>{' '}
            <span className="animate-blur-rise block text-gradient-muted" style={delay(170)}>
              &amp; Practical Tech Courses
            </span>{' '}
            <span className="animate-blur-rise block" style={delay(260)}>
              <span className="text-gradient-accent">in Sylhet, Bangladesh.</span>
            </span>
          </h1>

          <p
            className="animate-blur-rise mt-7 max-w-[600px] text-base leading-relaxed text-[var(--surface-muted)] sm:text-[18px]"
            style={delay(360)}
          >
            Live, mentor-led classes in Bangla + English for students, beginners and career
            switchers: small batches, real projects, portfolio work and{' '}
            <HandUnderline draw="load" delay={1200}>
              <span className="text-[var(--text-strong)]">honest career guidance</span>
            </HandUnderline>
            .
          </p>
          <p
            className="animate-blur-rise mt-3 max-w-[600px] font-bangla text-[15px] leading-relaxed text-[var(--surface-muted)]"
            style={delay(420)}
          >
            সিলেট থেকেই AI, Web Development ও ব্যবহারিক tech skill শিখে portfolio তৈরি করুন — আয়ের
            গ্যারান্টি ছাড়াই।
          </p>

          <ul
            className="animate-blur-rise mt-7 flex flex-wrap gap-x-6 gap-y-2.5 text-[13px] text-[var(--surface-muted)]"
            style={delay(480)}
          >
            <li className="inline-flex items-center gap-2">
              <IconUsers
                size={15}
                aria-hidden="true"
                className="text-[var(--color-brand-accent-readable)]"
              />
              Live mentor-led classes
            </li>
            <li className="inline-flex items-center gap-2">
              <IconLayers
                size={15}
                aria-hidden="true"
                className="text-[var(--color-brand-accent-readable)]"
              />
              Max {enrollment.seats} students per batch
            </li>
            <li className="inline-flex items-center gap-2">
              <IconGlobe
                size={15}
                aria-hidden="true"
                className="text-[var(--color-brand-accent-readable)]"
              />
              Bangla + English
            </li>
          </ul>

          <div className="animate-blur-rise mt-9 flex flex-wrap gap-3" style={delay(560)}>
            <Link to="/free-career-orientation" className={PRIMARY_PILL}>
              <span
                aria-hidden="true"
                className="shine-sweep pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/50 to-transparent"
              />
              Free Career Orientation Class
              <IconArrowRight
                size={16}
                aria-hidden="true"
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
            <Link to="/courses" className={GLASS_PILL}>
              Explore the courses
              <IconArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>

          <TrustNotes
            className="animate-blur-rise mt-7"
            items={[
              'Free orientation class first',
              'Installment support',
              'Honest guidance, no income promises',
            ]}
          />
        </div>

        <HeroPhoto enrollmentSeats={enrollment.seats} />
      </div>

      <div className="relative mx-auto mt-20 max-w-[1240px] px-4 sm:mt-24 sm:px-6">
        <p className="flex items-center gap-4 font-code text-[11px] uppercase tracking-[0.22em] text-[var(--surface-muted)]">
          <span
            aria-hidden="true"
            className="h-px flex-1 bg-gradient-to-r from-transparent to-[var(--surface-border-strong)]"
          />
          Tools you&apos;ll build with
          <span
            aria-hidden="true"
            className="h-px flex-1 bg-gradient-to-l from-transparent to-[var(--surface-border-strong)]"
          />
        </p>
        <div className="mt-7 grid gap-3">
          {rows.map((row, index) => (
            <Marquee
              key={row.label}
              label={row.label}
              items={row.tools}
              itemKey={(tool) => tool.name}
              renderItem={(tool) => <ToolChip tool={tool} />}
              duration={64}
              reverse={index % 2 === 1}
            />
          ))}
        </div>
        <ul className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-[22px] border border-[var(--surface-border)] bg-[var(--surface-border)] md:grid-cols-4">
          {[
            { value: 3, suffix: '', label: 'Open programs this intake' },
            { value: Number(enrollment.seats), suffix: '', label: 'Students per batch, at most' },
            { value: 3, suffix: '×', label: 'Live classes every week' },
            { value: 2, suffix: '', label: 'Languages: Bangla + English' },
          ].map((stat) => (
            <li
              key={stat.label}
              className="bg-[linear-gradient(180deg,#0e1013,#0a0c0e)] px-5 py-6 sm:px-7"
            >
              <p className="font-display text-[34px] font-semibold leading-none tracking-[-0.04em] text-[var(--text-strong)]">
                <CountUp value={stat.value} />
                {stat.suffix && (
                  <span className="text-[var(--color-brand-accent)]">{stat.suffix}</span>
                )}
              </p>
              <p className="mt-2 text-[13px] text-[var(--surface-muted)]">{stat.label}</p>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-center font-bangla text-sm text-[var(--surface-muted)]">
          ব্যবহারিক AI, Web Development ও career skill শিক্ষা - সিলেট থেকেই।
        </p>
      </div>
    </section>
  )
}

function SkillsSection() {
  return (
    <Chapter
      index={1}
      label="What you actually learn"
      id="skills"
      title={
        <>
          <Muted>Watching tutorials is easy.</Muted>
          <br />
          Building is the <Accent>skill.</Accent>
        </>
      }
    >
      <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
        <div className="flex flex-col gap-6">
          <Reveal>
            <p className="text-base leading-relaxed text-[var(--surface-muted)] sm:text-lg">
              Every Skillo course is built around making real things: an agent that answers WhatsApp
              messages, a web app with payments, a mobile app with AI features.
            </p>
          </Reveal>
          <Reveal delay={60}>
            <p className="text-base leading-relaxed text-[var(--surface-muted)] sm:text-lg">
              Videos explain a tool.{' '}
              <span className="text-[var(--text-strong)] underline decoration-[var(--marker)] decoration-2 underline-offset-[6px]">
                You learn it by shipping with it
              </span>
              , with a mentor checking your work.
            </p>
          </Reveal>
          <div className="mt-4 hidden lg:block">
            <HandNote className="-rotate-2">
              the skills behind every project you&apos;ll build
            </HandNote>
            <HandArrow
              variant="swoosh-right"
              className="ml-24 mt-1 h-14 w-36 text-[var(--marker)]"
            />
          </div>
        </div>
        <Reveal delay={80}>
          <ol className="grid gap-px overflow-hidden rounded-[22px] border border-[var(--surface-border)] bg-[var(--surface-border)] sm:grid-cols-2 lg:grid-cols-3">
            {SKILLS.map((skill, index) => (
              <li
                key={skill.title}
                className="group relative bg-[#0b0d10] p-6 transition-colors duration-500 hover:bg-[#101317] sm:p-7"
              >
                <span className="font-code text-[11px] text-[var(--surface-muted)]">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-6 font-display text-lg font-semibold tracking-[-0.01em] text-[var(--text-strong)] transition-colors duration-300 group-hover:text-[var(--color-brand-accent-readable)]">
                  {skill.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-[var(--surface-muted)]">
                  {skill.body}
                </p>
                <span
                  aria-hidden="true"
                  className="absolute inset-x-6 bottom-0 h-px scale-x-0 bg-gradient-to-r from-transparent via-[var(--color-brand-accent)] to-transparent transition-transform duration-500 group-hover:scale-x-100"
                />
              </li>
            ))}
          </ol>
        </Reveal>
      </div>
    </Chapter>
  )
}

function WeekSection() {
  return (
    <section aria-labelledby="week" className="relative py-20 sm:py-24">
      <div className="mx-auto grid max-w-[1240px] gap-14 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
        <div className="lg:sticky lg:top-32 lg:self-start">
          <Reveal>
            <SectionLabel index={2}>A week in a batch</SectionLabel>
          </Reveal>
          <Reveal delay={80}>
            <h2
              id="week"
              className="mt-8 text-balance font-display text-[clamp(32px,4.4vw,56px)] font-semibold leading-[1.04] tracking-[-0.04em] text-[var(--text-strong)]"
            >
              <Muted>Classes end.</Muted>
              <br />
              Learning <Accent>doesn&apos;t.</Accent>
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[var(--surface-muted)] sm:text-lg">
              A typical week in a live batch: a class, a build, a question, a review and a demo.
              Small batches mean someone actually reads your work.
            </p>
          </Reveal>
        </div>
        <div className="relative rounded-[28px] border border-[var(--surface-border)] bg-[linear-gradient(180deg,#0d0f12,#090a0c)] p-5 sm:p-8">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-[28px] bg-[radial-gradient(ellipse_at_top_right,rgba(0,229,255,0.07),transparent_55%)]"
          />
          <p className="relative mb-8 flex items-center gap-2 font-code text-[11px] uppercase tracking-[0.2em] text-[var(--surface-muted)]">
            <span
              aria-hidden="true"
              className="size-1.5 rounded-full bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.9)]"
            />
            # batch-5 · a typical week
          </p>
          <div className="relative">
            <StoryTimeline
              label="A typical week in a Skillo batch"
              items={[
                {
                  label: 'Sat · 10:00',
                  tone: 'accent',
                  channel: '#live-class',
                  title: 'Live class: connect an AI agent to WhatsApp',
                  body: 'The mentor builds it on screen, step by step, and you build along on your laptop.',
                },
                {
                  label: 'Sat · 12:10',
                  tone: 'success',
                  title: 'Your first workflow runs',
                  body: 'Practice time in class, with help at your desk.',
                  lines: [
                    { text: '▸ n8n workflow: whatsapp-agent', tone: 'info' },
                    { text: '✓ webhook connected', tone: 'ok' },
                    { text: '✓ reply sent in 1.4s', tone: 'ok' },
                  ],
                },
                {
                  label: 'Mon · 21:14',
                  tone: 'message',
                  author: { name: 'You', initials: 'YOU' },
                  body: 'My workflow stops at the Google Sheets step. What am I missing?',
                },
                {
                  label: 'Mon · 21:32',
                  tone: 'alert',
                  author: { name: 'Mentor', initials: 'MS', highlight: true },
                  body: 'The sheet ID has a space at the end. Remove it and run the workflow again.',
                  typing: true,
                },
                {
                  label: 'Wed · 18:00',
                  tone: 'success',
                  title: 'Task approved, with feedback',
                  body: '“Clean workflow. Next step: give the agent memory so it remembers the customer.”',
                },
                {
                  label: 'Fri · 19:00',
                  tone: 'accent',
                  title: 'Demo time',
                  body: (
                    <>
                      You show
                      <span className="mx-3 inline-block">
                        <HandCircle>
                          <span className="text-[var(--text-strong)]">your agent</span>
                        </HandCircle>
                      </span>
                      to the batch, and it becomes a portfolio project.
                    </>
                  ),
                },
              ]}
            />
          </div>
          <HandNote className="relative mt-10 text-right text-xl">
            small batches mean a mentor actually reads your work
          </HandNote>
        </div>
      </div>
    </section>
  )
}

const PORTAL_FEATURES = [
  {
    title: 'Modules open in order',
    body: 'Finish the lessons, tasks and test in one module and the next one opens.',
  },
  {
    title: 'Notes and practice after class',
    body: 'Read the lesson notes again and practise with short guided exercises.',
  },
  {
    title: 'Module tests with instant results',
    body: 'Most questions are marked right away. Your mentor marks written and code answers.',
  },
  {
    title: 'Feedback on every task',
    body: 'Submit your work in the portal and get comments you can act on.',
  },
]

function PortalSection() {
  const fullStack = courses.find((course) => course.slug === 'full-stack-ai-web-development')
  if (!fullStack) return null

  return (
    <Chapter
      index={3}
      label="The student portal"
      id="portal"
      title={
        <>
          <Muted>Between classes,</Muted> the portal keeps you <Accent>on track.</Accent>
        </>
      }
      intro="Enrolled students sign in to a portal built for their batch, so they always know what to study next and how far they have come."
    >
      <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <Reveal>
          <ul className="grid gap-3">
            {PORTAL_FEATURES.map((feature, index) => (
              <li
                key={feature.title}
                className="flex gap-4 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5"
              >
                <span className="font-code text-[11px] leading-6 text-[var(--color-brand-accent-readable)]">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span>
                  <span className="block font-display text-[17px] font-semibold text-[var(--text-strong)]">
                    {feature.title}
                  </span>
                  <span className="mt-1 block text-sm leading-relaxed text-[var(--surface-muted)]">
                    {feature.body}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-[13px] leading-relaxed text-[var(--surface-muted)]">
            Lesson notes, practice and module tests are added course by course, starting with{' '}
            <Link
              to={`/courses/${fullStack.slug}`}
              className="font-semibold text-[var(--color-brand-accent-readable)] underline-offset-4 hover:underline"
            >
              {fullStack.title}
            </Link>
            .
          </p>
        </Reveal>
        <Reveal delay={80}>
          <figure className="relative">
            <div
              aria-hidden="true"
              className="absolute -inset-10 rounded-[3rem] bg-[radial-gradient(circle_at_60%_40%,rgba(0,229,255,0.14),transparent_65%)]"
            />
            <PortalMock
              className="relative"
              courseTitle={fullStack.title}
              modules={fullStack.curriculum.map((module) => module.title)}
            />
            <figcaption className="relative mt-4 text-center text-xs text-[var(--surface-muted)]">
              A preview of the student portal. Your own page shows your batch and your progress.
            </figcaption>
          </figure>
        </Reveal>
      </div>
    </Chapter>
  )
}

function ProgramsSection({ featured, batchLabel }: { featured: Course[]; batchLabel: string }) {
  const spotlightRef = useSpotlight<HTMLDivElement>()

  return (
    <Chapter
      index={4}
      label="Open programs"
      id="open-paths"
      title={
        <>
          <Muted>Three paths open for</Muted> <Accent>{batchLabel}.</Accent>
        </>
      }
      intro="Each one is a live cohort with projects, mentor feedback and a clear syllabus. Pick the one that fits where you are today."
    >
      <div ref={spotlightRef} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {featured.map((course, index) => (
          <Reveal key={course.slug} delay={index * 90} className="h-full">
            <CourseCard course={course} featured={Boolean(course.flagship)} />
          </Reveal>
        ))}
      </div>
      <div className="mt-12 flex justify-center">
        <Link
          to="/courses"
          className="group inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.03] px-5 py-2.5 text-sm font-semibold text-[var(--text-strong)] no-underline transition-colors hover:border-white/25 hover:bg-white/[0.06]"
        >
          View all programs
          <IconArrowRight
            size={14}
            aria-hidden="true"
            className="transition-transform group-hover:translate-x-0.5"
          />
        </Link>
      </div>
    </Chapter>
  )
}

function PathFinderSection() {
  const options = PERSONAS.map((persona) => {
    const course = courses.find((item) => item.slug === persona.slug)
    return {
      id: persona.id,
      label: persona.label,
      icon: persona.icon,
      panel: course ? (
        <div className={cn(CARD, 'h-full p-6 sm:p-8')}>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -right-20 -top-20 size-64 rounded-full bg-[radial-gradient(circle,rgba(0,229,255,0.16),transparent_65%)]"
          />
          <p className="relative font-code text-[11px] uppercase tracking-[0.2em] text-[var(--color-brand-accent-readable)]">
            Recommended first course
          </p>
          <p className="relative mt-4 font-display text-[clamp(24px,2.6vw,32px)] font-semibold leading-tight tracking-[-0.03em] text-[var(--text-strong)]">
            {course.title}
          </p>
          <p className="relative mt-4 text-[15px] leading-relaxed text-[var(--surface-muted)]">
            {persona.reason}
          </p>
          <div className="relative mt-6 flex flex-wrap gap-1.5">
            {course.tags.slice(0, 5).map((tag) => (
              <ToolTag key={tag} name={tag} />
            ))}
          </div>
          <div className="relative mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={`/courses/${course.slug}`}
              className="group inline-flex min-h-11 items-center gap-2 rounded-full bg-[var(--color-brand-accent)] px-5 text-sm font-semibold text-[var(--accent-ink)] no-underline transition-transform hover:-translate-y-px"
            >
              {course.status === 'open' ? 'See the course' : 'See what is coming'}
              <IconArrowRight
                size={14}
                aria-hidden="true"
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
            <span className="text-[13px] text-[var(--surface-muted)]">
              {course.status === 'open'
                ? `${course.duration} · ${course.batchSize}`
                : 'Coming soon'}
            </span>
          </div>
        </div>
      ) : null,
    }
  })

  return (
    <Chapter
      index={5}
      label="Course roadmap by student type"
      id="course-path"
      title={
        <>
          <Muted>Not sure which course</Muted> is right for <Accent>you?</Accent>
        </>
      }
    >
      <PathFinder label="Choose what sounds like you" options={options} />
      <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4">
        <Link to="/free-career-counselling" className={GLASS_PILL}>
          Get free career counselling
          <IconArrowRight size={15} aria-hidden="true" />
        </Link>
        <HandNote className="text-xl">start where you are, not where others are</HandNote>
      </div>
    </Chapter>
  )
}

function JoinSection() {
  const now = useCurrentTime()
  const enrollment = getEnrollment(now)
  const whatsappKeywords = [
    { keyword: 'AI', label: 'AI Agent Builder roadmap', href: buildCampaignWhatsAppUrl('ai', now) },
    { keyword: 'WEB', label: 'Full Stack roadmap', href: buildCampaignWhatsAppUrl('web', now) },
    {
      keyword: 'AI CLASS',
      label: 'Free orientation invite',
      href: buildCampaignWhatsAppUrl('aiClass', now),
    },
  ]

  return (
    <Chapter
      index={6}
      label={siteConfig.campaign.name}
      id="how-joining-works"
      title={
        <>
          <Muted>Free class first.</Muted>
          <br />
          Admission decision <Accent>second.</Accent>
        </>
      }
      intro="Join the free live career class to see practical AI automation, full stack and mobile project demos before choosing a paid course. No pressure: you decide after the class."
    >
      <div className="relative">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden lg:block">
          {[
            { label: 'n8n', className: 'left-[2%] top-[12%]' },
            { label: 'Portfolio', className: 'right-[4%] top-[34%]' },
            { label: 'Next.js', className: 'left-[6%] top-[62%]' },
            { label: 'Mentor feedback', className: 'right-[8%] bottom-[6%]' },
          ].map((ghost) => (
            <span
              key={ghost.label}
              data-ghost={ghost.label}
              className={cn(
                'absolute rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 font-code text-xs text-white/30 blur-[2px] before:content-[attr(data-ghost)]',
                ghost.className,
              )}
            />
          ))}
        </div>
        <RoadmapPath
          label="How joining Skillo works"
          steps={[
            {
              title: 'Join the free orientation class',
              body: 'See AI automation, full stack and mobile project demos before choosing a paid course.',
              meta: 'Free · live',
            },
            {
              title: 'Talk it through in counselling',
              body: 'Compare paths and ask about schedule and payment. Guardians are welcome.',
              meta: 'WhatsApp or the office',
            },
            {
              title: `Book your seat with ${siteConfig.campaign.seatBookingToken}`,
              body: `Installment support: ${siteConfig.campaign.installment}.`,
              meta: `Max ${enrollment.seats} seats`,
            },
            {
              title: `${enrollment.batchName} starts ${enrollment.nextBatchDate}`,
              body: 'Live classes, tasks and mentor feedback from the first week.',
              meta: 'Live in Sylhet',
            },
          ]}
        />
      </div>

      <div className="mt-16 grid gap-5 lg:grid-cols-[1fr_1.4fr]">
        <Reveal className="h-full">
          <div className={cn(CARD, 'h-full p-6 sm:p-7')}>
            <h3 className="font-display text-lg font-semibold text-[var(--text-strong)]">
              WhatsApp keywords
            </h3>
            <p className="mt-1 text-sm text-[var(--surface-muted)]">
              Send one word, get the right roadmap.
            </p>
            <ul className="mt-5 grid gap-2.5">
              {whatsappKeywords.map((item) => (
                <li key={item.keyword}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between gap-4 rounded-xl border border-white/[0.07] bg-white/[0.02] px-4 py-3 no-underline transition-colors hover:border-[#25D366]/40 hover:bg-[#25D366]/[0.06]"
                  >
                    <span>
                      <span className="block text-sm font-semibold text-[var(--text-strong)]">
                        Send “{item.keyword}”
                      </span>
                      <span className="mt-0.5 block text-xs text-[var(--surface-muted)]">
                        {item.label}
                      </span>
                    </span>
                    <IconMessageCircle
                      size={16}
                      aria-hidden="true"
                      className="shrink-0 text-[#25D366] transition-transform group-hover:scale-110"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={80} className="h-full">
          <div className={cn(CARD, 'grid h-full gap-px bg-[var(--surface-border)] sm:grid-cols-3')}>
            {[
              {
                title: 'Seat booking',
                body: `${siteConfig.campaign.seatBookingToken} token. Installments: ${siteConfig.campaign.installment}.`,
              },
              {
                title: 'Referral benefit',
                body: `${siteConfig.campaign.referral.referrerReward}. Referred student gets ${siteConfig.campaign.referral.referredStudentDiscount}.`,
              },
              {
                title: 'Founder scholarship',
                body: `${siteConfig.campaign.scholarship.seatsPerCourse}. Applicants submit a 1-minute video and keep 90% attendance if selected.`,
              },
            ].map((item, index) => (
              <div key={item.title} className="bg-[#0b0d10] p-6">
                <span className="font-code text-[11px] text-[var(--color-brand-accent-readable)]">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-3 font-display text-base font-semibold text-[var(--text-strong)]">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--surface-muted)]">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to={siteConfig.campaign.path} className={GLASS_PILL}>
          See the {enrollment.batchMonth} batch plan <IconArrowRight size={14} aria-hidden="true" />
        </Link>
        <Link to="/admissions/referral-scholarship" className={GLASS_PILL}>
          Admission support policy <IconArrowRight size={14} aria-hidden="true" />
        </Link>
      </div>
    </Chapter>
  )
}

function WhySection() {
  const now = useCurrentTime()
  const enrollment = getEnrollment(now)
  const spotlightRef = useSpotlight<HTMLDivElement>()
  const seats = Number(enrollment.seats)

  return (
    <Chapter
      index={7}
      label="Why students choose us"
      id="features"
      title={
        <>
          <Muted>Small batches.</Muted> Real <Accent>attention.</Accent>
        </>
      }
      intro="Built for careers, not just certificates: guided practice, honest expectations and a mentor who sees your work."
    >
      <div ref={spotlightRef} className="grid gap-4 md:grid-cols-6">
        <Reveal className="md:col-span-4">
          <div className={cn(CARD, 'h-full p-6 sm:p-8')}>
            <div className="grid gap-8 sm:grid-cols-[1fr_1.1fr] sm:items-center">
              <div>
                <p className="font-code text-[11px] uppercase tracking-[0.2em] text-[var(--surface-muted)]">
                  Batch size
                </p>
                <h3 className="mt-4 font-display text-[28px] font-semibold leading-tight tracking-[-0.03em] text-[var(--text-strong)]">
                  Max <span className="text-gradient-accent">{seats}</span> students per batch
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[var(--surface-muted)]">
                  Small enough that your mentor knows your name, your project and where you get
                  stuck.
                </p>
              </div>
              <SeatMeter seats={seats} />
            </div>
          </div>
        </Reveal>
        <Reveal delay={60} className="md:col-span-2">
          <div className={cn(CARD, 'h-full p-6')}>
            <ClassCallMock />
            <h3 className="mt-5 font-display text-lg font-semibold text-[var(--text-strong)]">
              Live, not recorded
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--surface-muted)]">
              Ask questions while the mentor is coding, not in a comment nobody reads.
            </p>
          </div>
        </Reveal>
        <Reveal className="md:col-span-2">
          <div className={cn(CARD, 'h-full p-6')}>
            <div aria-hidden="true" className="h-[52px] overflow-hidden">
              <div className="animate-flip-word">
                <p className="flex h-[52px] items-center font-bangla text-[26px] font-semibold text-[var(--text-strong)]">
                  ক্লাস হবে বাংলায়
                </p>
                <p className="flex h-[52px] items-center font-display text-[26px] font-semibold tracking-[-0.02em] text-gradient-accent">
                  Code in English
                </p>
              </div>
            </div>
            <h3 className="mt-5 font-display text-lg font-semibold text-[var(--text-strong)]">
              Bangla + English
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--surface-muted)]">
              Understand every concept in Bangla while building real technical English.
            </p>
          </div>
        </Reveal>
        <Reveal delay={60} className="md:col-span-2">
          <div className={cn(CARD, 'group h-full p-6')}>
            <ProjectStack />
            <h3 className="mt-5 font-display text-lg font-semibold text-[var(--text-strong)]">
              Projects you can demo
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--surface-muted)]">
              Every course ends with work you can demo in class, interviews or your own business.
            </p>
          </div>
        </Reveal>
        <Reveal delay={120} className="md:col-span-2">
          <div className={cn(CARD, 'h-full p-6')}>
            <SupportChat />
            <h3 className="mt-5 font-display text-lg font-semibold text-[var(--text-strong)]">
              WhatsApp support
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--surface-muted)]">
              Stuck at home? Ask in the batch group and keep moving.
            </p>
          </div>
        </Reveal>
        <Reveal className="md:col-span-6">
          <div className={cn(CARD, 'p-6 sm:p-8')}>
            <div className="grid gap-6 md:grid-cols-[1fr_1.4fr] md:items-center">
              <div>
                <h3 className="font-display text-[22px] font-semibold tracking-[-0.02em] text-[var(--text-strong)]">
                  Pay in two parts
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--surface-muted)]">
                  Installment support: {siteConfig.campaign.installment}. Final details are agreed
                  in counselling.
                </p>
              </div>
              <InstallmentBar first="50% before class" second="50% by Week 4" />
            </div>
          </div>
        </Reveal>
      </div>
    </Chapter>
  )
}

function MentorSection() {
  const tiltRef = useTilt<HTMLDivElement>(5)

  return (
    <Chapter
      index={8}
      label="Your mentor"
      id="mentor"
      title={
        <>
          <Muted>Taught by an engineer,</Muted> not a <Accent>slideshow.</Accent>
        </>
      }
    >
      <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <Reveal>
          <div ref={tiltRef} className="relative mx-auto max-w-[460px] [perspective:1200px]">
            <div
              aria-hidden="true"
              className="absolute -inset-8 rounded-[3rem] bg-[radial-gradient(circle_at_50%_50%,rgba(252,211,77,0.12),transparent_62%)]"
            />
            <figure className="relative overflow-hidden rounded-[30px] border border-white/10 shadow-[0_50px_120px_-40px_rgba(0,0,0,0.95)] transition-transform duration-500 ease-[var(--ease-out-expo)] [transform:rotateX(var(--tilt-x,0deg))_rotateY(var(--tilt-y,0deg))]">
              <img
                src="/images/speaking.jpg"
                width={2048}
                height={1536}
                loading="lazy"
                alt="Minhaj Sadik working on a laptop"
                className="aspect-[4/5] w-full origin-[72%_55%] scale-[1.35] object-cover object-[72%_50%] [filter:saturate(0.88)_contrast(1.06)_brightness(0.8)]"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,8,10,0.55),transparent_35%),radial-gradient(ellipse_at_60%_45%,transparent_40%,rgba(7,8,10,0.55))]"
              />
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#07080a] to-transparent"
              />
              <figcaption className="absolute bottom-5 left-5 right-5">
                <span className="text-hand block text-2xl text-[var(--marker)]">
                  ask me anything in the free class
                </span>
              </figcaption>
            </figure>
          </div>
        </Reveal>
        <div>
          <Reveal>
            <p className="font-display text-[clamp(26px,3vw,36px)] font-semibold leading-tight tracking-[-0.03em] text-[var(--text-strong)]">
              Hi, I&apos;m Minhaj Sadik.
            </p>
          </Reveal>
          <Reveal delay={60}>
            <p className="mt-5 text-base leading-relaxed text-[var(--surface-muted)] sm:text-lg">
              I am a Sylhet-based backend and DevOps engineer, and I founded Skillo Academy to teach
              with a production-first mindset. My courses focus on shipping useful projects, not
              only watching tutorials.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {[
                { icon: <IconTerminal size={16} />, text: 'Backend and DevOps engineer' },
                { icon: <IconUsers size={16} />, text: 'Teaches every live batch' },
                { icon: <IconGlobe size={16} />, text: 'Classes in Bangla + English' },
                { icon: <IconBriefcase size={16} />, text: 'Projects over certificates' },
              ].map((item) => (
                <li
                  key={item.text}
                  className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] px-4 py-3 text-sm text-[var(--surface-fg)]"
                >
                  <span aria-hidden="true" className="text-[var(--color-brand-accent-readable)]">
                    {item.icon}
                  </span>
                  {item.text}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={180}>
            <div className="mt-8 flex flex-wrap gap-2">
              {[
                {
                  label: 'Website',
                  href: siteConfig.social.founderWebsite,
                  icon: <IconGlobe size={15} />,
                },
                {
                  label: 'GitHub',
                  href: siteConfig.social.founderGithub,
                  icon: <BrandMark icon={SOCIAL_ICONS.github} tone="mono" size={15} />,
                },
                {
                  label: 'LinkedIn',
                  href: siteConfig.social.founderLinkedIn,
                  icon: <IconBriefcase size={15} />,
                },
                {
                  label: 'YouTube',
                  href: siteConfig.social.founderYoutube,
                  icon: <BrandMark icon={SOCIAL_ICONS.youtube} tone="mono" size={15} />,
                },
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex min-h-10 items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 text-sm font-medium text-[var(--surface-fg)] no-underline transition-colors hover:border-white/25 hover:text-[var(--text-strong)]"
                >
                  <span
                    aria-hidden="true"
                    className="text-[var(--surface-muted)] group-hover:text-[var(--color-brand-accent-readable)]"
                  >
                    {link.icon}
                  </span>
                  {link.label}
                  <IconArrowUpRight
                    size={13}
                    aria-hidden="true"
                    className="text-[var(--surface-muted)]"
                  />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </Chapter>
  )
}

function PromiseSection() {
  return (
    <Chapter
      index={9}
      label="Honest promises"
      id="promises"
      title={
        <>
          <Muted>No fake promises.</Muted> Just <Gold>honest</Gold> teaching.
        </>
      }
    >
      <div className="grid gap-5 md:grid-cols-2">
        <Reveal className="h-full">
          <div className={cn(CARD, 'h-full p-6 sm:p-8')}>
            <p className="font-code text-[11px] uppercase tracking-[0.2em] text-green-300">
              What we promise
            </p>
            <ul className="mt-6 grid gap-4">
              {PROMISES.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-[15px] text-[var(--surface-fg)]"
                >
                  <span
                    aria-hidden="true"
                    className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-400/15 text-green-300"
                  >
                    <IconCheck size={12} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={80} className="h-full">
          <div className={cn(CARD, 'h-full p-6 sm:p-8')}>
            <p className="font-code text-[11px] uppercase tracking-[0.2em] text-red-300">
              What we never promise
            </p>
            <ul className="mt-6 grid gap-4">
              {NEVER.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-[15px] text-[var(--surface-fg)]"
                >
                  <span
                    aria-hidden="true"
                    className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-red-400/15 text-red-300"
                  >
                    <IconX size={12} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
      <Reveal>
        <div className="mx-auto mt-6 flex max-w-[980px] gap-3 rounded-2xl border border-[var(--marker)]/25 bg-[var(--marker)]/[0.05] p-5">
          <IconShieldCheck
            size={18}
            aria-hidden="true"
            className="mt-0.5 shrink-0 text-[var(--marker)]"
          />
          <p className="text-sm leading-relaxed text-[var(--surface-muted)]">
            <strong className="text-[var(--text-strong)]">Honest note:</strong>{' '}
            {siteConfig.trust.incomeDisclaimer}
          </p>
        </div>
      </Reveal>
      <Reveal>
        <p className="mt-8 text-center text-sm text-[var(--surface-muted)]">
          Studied with Minhaj Sadik?{' '}
          <a
            href={buildWhatsAppUrl(
              "Hi, I'm a past student and I'd like to share my testimonial for Skillo",
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[var(--color-brand-accent-readable)] underline-offset-4 hover:underline"
          >
            Share your story on WhatsApp
          </a>
          . We publish only real stories, with consent.
        </p>
      </Reveal>
    </Chapter>
  )
}

function FaqSection({ faqs }: { faqs: { question: string; answer: string }[] }) {
  return (
    <section aria-labelledby="faq" className="relative py-20 sm:py-24">
      <div className="mx-auto grid max-w-[1240px] gap-12 px-4 sm:px-6 lg:grid-cols-[0.75fr_1.25fr]">
        <div className="lg:sticky lg:top-32 lg:self-start">
          <Reveal>
            <SectionLabel index={10}>Student questions</SectionLabel>
          </Reveal>
          <Reveal delay={80}>
            <h2
              id="faq"
              className="mt-8 font-display text-[clamp(32px,4.4vw,56px)] font-semibold leading-[1.04] tracking-[-0.04em] text-[var(--text-strong)]"
            >
              Frequently asked <Accent>questions</Accent>
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 max-w-sm text-base leading-relaxed text-[var(--surface-muted)]">
              Still unsure? Ask on WhatsApp or send an enquiry, and the office will reply.
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-brand-accent-readable)] no-underline transition-[gap] hover:gap-3"
            >
              Send an enquiry <IconArrowRight size={14} aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={80}>
          <FaqList items={faqs} name="home-faq" />
        </Reveal>
      </div>
    </section>
  )
}

function FinalCta() {
  const now = useCurrentTime()
  const enrollment = getEnrollment(now)

  return (
    <section aria-labelledby="final-call" className="relative overflow-hidden py-28 sm:py-36">
      <Aurora tone="cta" grid={false} />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-dots [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]"
      />
      <div className="relative mx-auto max-w-[940px] px-4 text-center sm:px-6">
        <Reveal>
          <h2
            id="final-call"
            className="text-balance font-display text-[clamp(36px,5.6vw,68px)] font-semibold leading-[1.02] tracking-[-0.045em] text-[var(--text-strong)]"
          >
            <span className="text-gradient-white">Stop collecting tutorials.</span>
            <br />
            <Accent>Start building with a mentor.</Accent>
            <span className="mt-4 block font-bangla text-[0.42em] font-semibold tracking-normal text-[var(--surface-muted)]">
              Career-এ next step নিতে প্রস্তুত?
            </span>
          </h2>
        </Reveal>
        <Reveal delay={80}>
          <p className="mx-auto mt-7 max-w-xl text-lg text-[var(--surface-muted)]">
            Limited seats per cohort. Start with a clear roadmap, mentor support and practical
            projects.
          </p>
          <p className="mx-auto mt-2 max-w-xl font-bangla text-[15px] text-[var(--surface-muted)]">
            সীমিত সিট - এখনই ভর্তি হয়ে guidance নিয়ে confidence-এর সাথে skill build করুন।
          </p>
        </Reveal>
        <Reveal delay={140}>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/courses" className={WHITE_PILL}>
              Apply for a Program | ভর্তি শুরু করুন
              <IconArrowRight
                size={16}
                aria-hidden="true"
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
            <a
              href={buildWhatsAppUrl("Hi, I'm interested in Skillo courses")}
              target="_blank"
              rel="noopener noreferrer"
              className={GLASS_PILL}
            >
              <IconPhone size={16} aria-hidden="true" />
              Talk to an Advisor | হোয়াটসঅ্যাপে পরামর্শ নিন
            </a>
          </div>
          <TrustNotes
            className="mt-8 justify-center"
            items={[
              `Max ${enrollment.seats} students per batch`,
              'Guardians welcome',
              'Installment support',
            ]}
          />
        </Reveal>
      </div>
    </section>
  )
}

export function Home() {
  const now = useCurrentTime()
  const enrollment = getEnrollment(now)
  const homeFaqs = getHomeFaqs(enrollment.batchMonth)
  const featuredCourses = siteConfig.campaign.targetCourseSlugs
    .map((slug) => courses.find((course) => course.slug === slug))
    .filter((course): course is Course => Boolean(course))

  return (
    <>
      <SiteHeader />

      <main>
        <Hero />
        <SkillsSection />
        <WeekSection />
        <PortalSection />
        <ProgramsSection featured={featuredCourses} batchLabel={enrollment.batchLabel} />
        <PathFinderSection />
        <JoinSection />
        <WhySection />
        <MentorSection />
        <PromiseSection />
        <FaqSection faqs={homeFaqs} />
        <FinalCta />

        <section className="pb-6">
          <div className="mx-auto max-w-[1240px] px-4 sm:px-6">
            <div className="rounded-[24px] border border-[var(--surface-border)] bg-[var(--surface-card)]/60 p-6 sm:p-9">
              <h2 className="font-display text-2xl font-semibold tracking-[-0.02em] text-[var(--text-strong)] sm:text-3xl">
                {siteConfig.brand.primarySearchIdentity}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-[var(--surface-muted)] sm:text-base">
                {siteConfig.brand.entityStatement} আমাদের লক্ষ্য: practical skill শেখানো, portfolio
                build করা, এবং students-দের studies ও career-এ এগিয়ে নিতে সাহায্য করা — আয় বা
                চাকরির গ্যারান্টি ছাড়াই।
              </p>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </>
  )
}
